import { GRID_CONFIG, sortByAuditUpdatedOn } from "../../config";
import LogoutIcon from "@mui/icons-material/Logout";
import axios from "axios";
import PropTypes from "prop-types";
import React from "react";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import {
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaSearch,
} from "react-icons/fa";
import { io } from "socket.io-client";

import { notifyUser } from "../../utils/notifyUser";

const socket = io(process.env.REACT_APP_API_SOCKET_URL);
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const SessionManager = ({ user }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState("");

    // Custom styles with blue header
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "50px",
                "&:hover": {
                    backgroundColor: "#e6f7ff",
                    borderLeft: "2px solid var(--navy)",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    // Fetch session data
    const fetchSessions = async () => {
        // Initialize empty sessions array
        let sessionsData = [];

        try {
            const response = await axios.get(
                `${BASE_URL}/api/sessions/get-user-sessions`,
                { withCredentials: true },
            );
            // If response returns data and it's an array, use it; otherwise keep empty array
            if(response.data && Array.isArray(response.data)) {
                sessionsData = response.data;
            }
        } catch(error) {
            console.error("Error fetching sessions:", error);
            notifyUser(null, "error", "Failed to load sessions", "toast", null, null, null, null, null, false, false, "top-center");
            // Keep sessionsData as empty array on error
        } finally {
            // Always set the sessions state with our sessionsData (either populated or empty)
            setSessions(sortByAuditUpdatedOn(sessionsData));
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, []);

    // Filter sessions based on search input and status
    const filteredSessions = Array.isArray(sessions)
        ? sessions.filter((session) => {
              const fullName = `${session.first_name || ""} ${
                  session.last_name || ""
              }`.trim();
              // Text search filter
              const matchesSearch =
                  (fullName &&
                      fullName.toLowerCase().includes(search.toLowerCase())) ||
                  (session.first_name &&
                      session.first_name
                          .toLowerCase()
                          .includes(search.toLowerCase())) ||
                  (session.last_name &&
                      session.last_name
                          .toLowerCase()
                          .includes(search.toLowerCase())) ||
                  (session.username &&
                      session.username
                          .toLowerCase()
                          .includes(search.toLowerCase())) ||
                  (session.ip_address &&
                      session.ip_address
                          .toLowerCase()
                          .includes(search.toLowerCase()));

              // Status filter
              const matchesStatus =
                  selectedStatus === "" || session.status === selectedStatus;

              // Return true if the session matches both the search and status filters
              return matchesSearch && matchesStatus;
          })
        : [];

        const handleExportSessionsCSV = () => {
            try {
                const rows = filteredSessions.map((s) => ({
                    Name: `${s.first_name || ""} ${s.last_name || ""}`.trim() || s.username || "",
                    Username: s.username || "",
                    IPAddress: s.ip_address || "",
                    LoginTime: s.login_time ? new Date(s.login_time).toLocaleString() : "",
                    LogoutTime: s.logout_time ? new Date(s.logout_time).toLocaleString() : "",
                    Status: s.status || "",
                    SessionID: s.session_id || "",
                    UserID: s.user_id || "",
                }));

                if (!rows || rows.length === 0) {
                    notifyUser(null, "info", "No sessions to export", "toast", null, null, null, null, null, false, false, "top-center");
                    return;
                }

                const header = Object.keys(rows[0]).join(",");
                const csvBody = rows
                    .map((r) =>
                        Object.values(r)
                            .map((v) => `"${String(v).replace(/\"/g, '""')}"`)
                            .join(","),
                    )
                    .join("\n");

                const csv = `${header}\n${csvBody}`;
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `sessions-${new Date().toISOString().slice(0, 10)}.csv`,
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                URL.revokeObjectURL(url);
            } catch (err) {
                notifyUser(null, "error", "Failed to export sessions", "toast", null, null, null, null, null, false, false, "top-center");
            }
        };

    // Pagination calculations
    const totalPages = Math.ceil(filteredSessions.length / perPage);
    const paginatedData = filteredSessions.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, filteredSessions.length);
    const totalRecords = filteredSessions.length;

    // Handle force logout
    const handleForceLogout = async (sessionId) => {
        try {
            await axios.post(
                `${BASE_URL}/api/sessions/force-logout/${sessionId}`,
                {},
                { withCredentials: true },
            );
            socket.emit("force_logout", { sessionId });
            notifyUser(null, "success", "User logged out successfully", "toast", null, null, null, null, null, false, false, "top-center");
        } catch(error) {
            if(error.response && error.response.status === 404) {
                notifyUser(null, "info", "Session already offline or not found.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to force logout", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            // Always refresh session list after attempting force logout
            fetchSessions();
        }
    };

    // Custom Pagination Component
    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {filteredSessions.length === 0 ? 0 : fromRecord} to{" "}
                {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1 || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages || loading}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    // Define table columns with adjusted widths
    const columns = [
        {
            name: "Name",
            selector: (row) =>
                `${row.first_name || ""} ${row.last_name || ""}`.trim(),
            cell: (row) => {
                const fullName = `${row.first_name || ""} ${
                    row.last_name || ""
                }`.trim();
                // Fallback to username if first_name and last_name are not available
                return fullName || row.username || "N/A";
            },
            sortable: true,
            width: "20%",
        },
        {
            name: "IP Address",
            selector: (row) => row.ip_address,
            width: "15%",
        },
        {
            name: "Login Time",
            selector: (row) => new Date(row.login_time).toLocaleString(),
            width: "20%",
        },
        {
            name: "Logout Time",
            selector: (row) =>
                row.logout_time
                    ? new Date(row.logout_time).toLocaleString()
                    : "Active",
            cell: (row) =>
                row.logout_time ? (
                    new Date(row.logout_time).toLocaleString()
                ) : (
                    <span style={{ color: "#28a745", fontStyle: "italic" }}>
                        Active
                    </span>
                ),
            width: "20%",
        },
        {
            name: "Status",
            selector: (row) => row.status,
            cell: (row) => (
                <span
                    style={{
                        color: row.status === "online" ? "#28a745" : "#6c757d",
                        fontWeight: row.status === "online" ? "500" : "normal",
                    }}
                >
                    {row.status}
                </span>
            ),
            width: "10%",
        },
        {
            name: "Actions",
            cell: (row) => {
                return row.status === "online" && row.user_id !== user?.id ? (
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            handleForceLogout(row.session_id);
                        }}
                        style={{
                            color: "#dc3545",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "flex",
                            justifyContent: "center",
                            ":hover": {
                                transform: "scale(1.2)",
                            },
                        }}
                        title="Force Logout"
                    >
                        <LogoutIcon fontSize="small" />
                    </div>
                ) : (
                    <span style={{ color: "#6c757d", fontStyle: "italic" }}>
                        -
                    </span>
                );
            },
            width: "15%",
            ignoreRowClick: true,
            right: true,
        },
    ];

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                USER SESSIONS
            </h2>

            {/* Search and Pagination Controls */}
            <div className="aqi-grid-toolbar">
                {/* Rows per page - Left */}
                <div className="mr-2">
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* Status filter - Middle */}
                <div className="mr-2">
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#F5F7FA",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="online">Online</option>
                        <option value="offline">Offline</option>
                    </select>
                </div>

                <div className="aqi-grid-search">
                        <FaSearch
                            className="absolute left-3 top-2.5"
                            style={{ color: "var(--navy)" }}
                        />
                        <input
                            type="text"
                            placeholder="Search names or IP addresses..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                paddingLeft: "2.5rem",
                                paddingRight: "0.75rem",
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                                borderRadius: "0.375rem",
                                border: "2px solid #C2D7E2",
                                backgroundColor: "#F5F7FA",
                                transition: "all 0.2s ease",
                                outline: "none",
                                color: "var(--navy)",
                            }}
                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        />
                </div>
                <div className="aqi-grid-toolbar-actions">
                    <button
                        onClick={handleExportSessionsCSV}
                        title="Export CSV"
                        className="aqi-grid-button aqi-grid-button--primary"
                    >
                        Export
                    </button>
                </div>
            </div>

            <DataTable
                columns={columns}
                data={paginatedData}
                customStyles={customStyles}
                progressPending={loading}
                pagination={false}
                pointerOnHover
                responsive
                striped
                onRowClicked={(row) => setSelectedRow(row)}
                conditionalRowStyles={[
                    {
                        when: (row) =>
                            selectedRow &&
                            row.session_id === selectedRow.session_id,
                        style: {
                            backgroundColor: "#e6f7ff",
                            borderLeft: "4px solid var(--navy)",
                            color: "var(--navy)",
                            fontWeight: "500",
                            boxShadow: "0 0 5px rgb(var(--navy-rgb) / 0.2)",
                        },
                    },
                ]}
            />

            {/* Custom Pagination UI */}
            <CustomPagination />
        </div>
    );
};

SessionManager.propTypes = {
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
};


export default SessionManager;
